import { useState } from 'react'

import { Box, Button, Icon, Text } from '@factorialco/deprecated-design-system'

import Callout from 'modules/core/components/Callout'
import Modal from 'modules/core/components/Modal'
import Selector from 'modules/core/components/Selector'
import TextField from 'modules/core/components/TextField'
import i18n from 'modules/core/lib/i18n'

import { EnumOption } from './EnumSelector'

type Props = {
  unrecognizedOptions: string[]
  validOptions: EnumOption[]
  onSave: (mappedValues: MappedValues) => void
  onClose: () => void
  translatedName: string
  calloutDescription?: string
}

export type MappedValues = Array<{
  unrecognizedValue?: string
  value?: string
} | null>

const GetOptions = (validOptions: EnumOption[]) => {
  return validOptions.map(({ id, name }) => {
    return (
      <Selector.Option key={id} value={id.toString()} label={name} searchValue={name}>
        {name}
      </Selector.Option>
    )
  })
}

const MapValuesModal = ({
  unrecognizedOptions,
  validOptions,
  onSave,
  onClose,
  translatedName,
  calloutDescription,
}: Props) => {
  const [mappedValues, setMappedValues] = useState<MappedValues>([])

  const onValueChange = (value: string, index: number) => {
    const newMappedValues = [...mappedValues]
    newMappedValues[index] = { unrecognizedValue: unrecognizedOptions[index], value }
    setMappedValues(newMappedValues)
  }

  const onSubmit = (values: MappedValues) => {
    onSave(values)
    onClose()
  }

  return (
    <Modal
      illustration="document-edit"
      title={i18n.t('importer_process.edition_step.map_modal.title', {
        type: translatedName,
      })}
      style={{ width: '538px', justifyContent: 'center', alignItems: 'center' }}
      onSubmit={() => onSubmit(mappedValues)}
      onClose={onClose}
      footer={
        <>
          <Button type="secondary" onClick={onClose}>
            {i18n.t('importer_process.edition_step.map_modal.buttons.cancel')}
          </Button>
          <Button type="primary" submit>
            {i18n.t('importer_process.edition_step.map_modal.buttons.save')}
          </Button>
        </>
      }
    >
      <Box
        flexDirection="column"
        gap="s24"
        style={{ overflow: 'hidden', width: '100%', height: '100%' }}
      >
        {calloutDescription && (
          <Callout
            title={i18n.t('importer_process.edition_step.map_modal.callout.title')}
            description={calloutDescription}
            type="warning"
          />
        )}
        <Box
          flexDirection="column"
          gap="s16"
          style={{
            width: '100%',
            height: '100%',
            maxHeight: '370px',
            overflowY: 'scroll',
            overflowX: 'hidden',
            boxSizing: 'content-box',
            paddingRight: '17px',
          }}
        >
          <Box flexDirection="row" justifyContent="spaceBetween">
            <Text.Normal color="grey1000">
              {i18n.t('importer_process.edition_step.map_modal.unrecognized_title', {
                type: translatedName,
              })}
            </Text.Normal>
            <Text.Normal color="grey1000">
              {i18n.t('importer_process.edition_step.map_modal.available_title', {
                type: translatedName,
              })}
            </Text.Normal>
          </Box>
          {unrecognizedOptions.map((option, index) => (
            <Box
              key={index}
              flexDirection="row"
              gap="s16"
              justifyContent="center"
              alignItems="center"
              style={{ boxSizing: 'border-box' }}
            >
              <TextField value={option} hideLabel readonly showEllipsis title={option} />
              <Box>
                <Icon.Medium icon="equals" color="grey600" />
              </Box>
              <Selector.Popover
                key={index}
                value={mappedValues[index]?.value ? [mappedValues[index]?.value] : []}
                blankLabel={i18n.t('common.empty')}
                onChange={(value) => onValueChange(value ? `${value}` : '', index)}
                placeholder={i18n.t('common.select_one')}
              >
                {GetOptions(validOptions)}
              </Selector.Popover>
            </Box>
          ))}
        </Box>
      </Box>
    </Modal>
  )
}

export default MapValuesModal
